import { type FormEvent, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import PublicShell, { Wordmark } from "@/components/PublicShell";
import { ErrorNote } from "@/components/AppShell";
import { auth, site } from "@/lib/api";
import { useAsync } from "@/lib/hooks";

/** The sign-in page. A list of doors, and an honest sentence when one shut. */
export default function Login() {
  const [params] = useSearchParams();
  const info = useAsync(() => site.info(), []);
  const next = params.get("next") ?? undefined;
  const closed = params.get("closed") === "1";
  const error = params.get("error");

  const providers = info.data?.providers ?? [];
  const real = providers.filter((p) => p.name !== "mock");
  const mock = providers.some((p) => p.name === "mock");

  return (
    <PublicShell contactEmail={info.data?.contact_email}>
      <div className="mx-auto max-w-sm pt-6">
        <div className="mb-6 flex justify-center">
          <Wordmark large />
        </div>

        <div className="card p-6">
          <h1 className="text-xl font-semibold tracking-tight">Sign in</h1>
          <p className="mt-1 text-sm text-(--color-ink-muted)">
            {info.data?.signup_mode === "open"
              ? "Use an account you already have. If it's your first time, that's all it takes to start."
              : "Use an account you already have."}
          </p>

          {closed && (
            <Notice>
              Pool Nerd isn't taking new accounts right now. If you were invited, sign in with the
              address that was invited — an account that already exists always gets in.
              {info.data?.contact_email && (
                <>
                  {" "}
                  Otherwise, ask at{" "}
                  <a className="underline" href={`mailto:${info.data.contact_email}`}>
                    {info.data.contact_email}
                  </a>
                  .
                </>
              )}
            </Notice>
          )}
          {error === "state" && (
            <Notice>That sign-in took too long, or was started in another tab. Try again.</Notice>
          )}
          {error === "declined" && (
            <Notice>You cancelled at the provider. Nothing was created.</Notice>
          )}
          {error === "provider" && (
            <Notice>The sign-in provider didn't answer properly. Try again in a moment.</Notice>
          )}
          {info.error && <ErrorNote message="Could not reach the server." />}

          <div className="mt-5 space-y-2">
            {real.map((p) => (
              <a
                key={p.name}
                href={auth.loginUrl(p.name, next)}
                className="btn btn-quiet w-full"
              >
                Continue with {p.label}
              </a>
            ))}
            {info.data && real.length === 0 && !mock && (
              <p className="text-sm text-(--color-status-high)">
                No sign-in provider is configured on this server yet.
              </p>
            )}
          </div>

          {mock && <DevSignIn next={next} />}

          <p className="mt-6 text-xs text-(--color-ink-faint)">
            Signing in creates a private account for your pool. What is stored and why is in the{" "}
            <Link to="/privacy" className="underline">
              privacy policy
            </Link>
            .
          </p>
        </div>
      </div>
    </PublicShell>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <p role="status" className="mt-4 rounded-xl bg-(--color-surface-sunken) p-3 text-sm">
      {children}
    </p>
  );
}

/**
 * Development only. The server offers the mock provider only when
 * ENABLE_MOCK_SSO is on, and refuses to start with it on behind https.
 */
function DevSignIn({ next }: { next?: string }) {
  const [name, setName] = useState("owner");

  function go(event: FormEvent) {
    event.preventDefault();
    const params = new URLSearchParams({ user: name.trim() || "owner" });
    if (next) params.set("next", next);
    window.location.assign(`/api/auth/login/mock?${params.toString()}`);
  }

  return (
    <form onSubmit={go} className="mt-5 border-t border-(--color-border-subtle) pt-4">
      <p className="unit">Development sign-in</p>
      <div className="mt-2 flex gap-2">
        <input
          aria-label="Name to sign in as"
          className="field"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button type="submit" className="btn btn-quiet shrink-0 text-sm">
          Sign in
        </button>
      </div>
    </form>
  );
}
